import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';
import { extractErrorMessage } from '../../../utils/errorUtils';
import type { ApiResponse } from '../../menu/types';
import { toCamelCase } from '../utils';

export type InvoiceDeliveryAttachment = {
  name: string;
  type: 'invoice' | 'timesheet';
};

export type InvoiceDeliveryPreview = {
  to: string;
  cc: string[];
  bcc?: string[];
  subject: string;
  message: string;
  clientName: string;
  billingPeriod: string;
  attachments: InvoiceDeliveryAttachment[];
};

export type InvoiceDeliveryPreviewRequest = {
  invoiceId: number;
  templateId?: string;
};

export type InvoiceDeliverySendRequest = {
  invoiceId: number;
  to: string;
  cc: string;
  subject: string;
  message: string;
  templateId?: string;
};

export type InvoiceDeliveryReplaceAttachmentRequest = {
  invoiceId: number;
  attachmentType: 'invoice' | 'timesheet';
  file: File;
};

export type InvoiceDeliveryResponse = {
  historyId: number;
  status: string;
  message?: string | null;
};

export type InvoiceDeliverySendResponse = InvoiceDeliveryResponse;

export type InvoiceDeliveryStatusResponse = InvoiceDeliveryResponse;

export const INVOICE_DELIVERY_API = '/umbraco/api/InvoiceDelivery';

const SEND_SUCCESS_MESSAGE = 'Invoice was successfully sent to the client';
const SEND_FAILURE_MESSAGE = 'Email was not sent. Please try again or contact support';

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const invoiceDeliveryApi = createApi({
  reducerPath: 'invoiceDeliveryApi',
  baseQuery,
  endpoints: (builder) => ({
    prepareInvoiceDeliveryPreview: builder.mutation<
      InvoiceDeliveryPreview,
      InvoiceDeliveryPreviewRequest
    >({
      query: (request) => ({
        url: `${INVOICE_DELIVERY_API}/PreparePreview`,
        method: 'POST',
        body: request,
      }),
      transformResponse: (response: ApiResponse<InvoiceDeliveryPreview>) => {
        if ((response.Code ?? response.code) === 1) {
          return toCamelCase(response.data) as InvoiceDeliveryPreview;
        }

        throw new Error(extractErrorMessage(response, 'Request failed.'));
      },
    }),
    sendInvoiceDelivery: builder.mutation<InvoiceDeliverySendResponse, InvoiceDeliverySendRequest>({
      query: (request) => ({
        url: `${INVOICE_DELIVERY_API}/Send`,
        method: 'POST',
        body: request,
      }),
      transformResponse: (response: ApiResponse<InvoiceDeliverySendResponse>) => {
        if ((response.Code ?? response.code) === 1) {
          return toCamelCase(response.data) as InvoiceDeliverySendResponse;
        }

        throw new Error(extractErrorMessage(response, SEND_FAILURE_MESSAGE));
      },
    }),
    replaceInvoiceDeliveryAttachment: builder.mutation<
      InvoiceDeliveryAttachment,
      InvoiceDeliveryReplaceAttachmentRequest
    >({
      query: ({ invoiceId, attachmentType, file }) => {
        const formData = new FormData();
        formData.append('invoiceId', String(invoiceId));
        formData.append('attachmentType', attachmentType);
        formData.append('file', file, file.name);

        return {
          url: `${INVOICE_DELIVERY_API}/ReplaceAttachment`,
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: ApiResponse<InvoiceDeliveryAttachment>) => {
        if ((response.Code ?? response.code) === 1) {
          return toCamelCase(response.data) as InvoiceDeliveryAttachment;
        }

        throw new Error(extractErrorMessage(response, 'Failed to replace attachment.'));
      },
    }),
    getInvoiceDeliveryStatus: builder.query<InvoiceDeliveryStatusResponse, number>({
      query: (historyId) => ({
        url: `${INVOICE_DELIVERY_API}/SendStatus`,
        params: { historyId },
      }),
      transformResponse: (response: ApiResponse<InvoiceDeliveryStatusResponse>) => {
        if ((response.Code ?? response.code) === 1) {
          return toCamelCase(response.data) as InvoiceDeliveryStatusResponse;
        }

        throw new Error(extractErrorMessage(response, 'Failed to get invoice delivery status.'));
      },
    }),
  }),
});

export const waitForInvoiceDeliveryResult = async (
  historyId: number,
  getStatus: (id: number) => Promise<InvoiceDeliveryStatusResponse>,
  options?: { intervalMs?: number; timeoutMs?: number }
): Promise<InvoiceDeliveryStatusResponse> => {
  const intervalMs = options?.intervalMs ?? 1500;
  const timeoutMs = options?.timeoutMs ?? 120_000;
  const startedAt = Date.now();

  while (Date.now() - startedAt < timeoutMs) {
    const status = await getStatus(historyId);
    const normalized = (status.status || '').toLowerCase();

    if (normalized === 'sent' || normalized === 'failed') {
      return {
        ...status,
        message:
          status.message || (normalized === 'sent' ? SEND_SUCCESS_MESSAGE : SEND_FAILURE_MESSAGE),
      };
    }

    await sleep(intervalMs);
  }

  throw new Error(SEND_FAILURE_MESSAGE);
};

export const {
  usePrepareInvoiceDeliveryPreviewMutation,
  useSendInvoiceDeliveryMutation,
  useReplaceInvoiceDeliveryAttachmentMutation,
  useLazyGetInvoiceDeliveryStatusQuery,
} = invoiceDeliveryApi;
